import {
  Box,
  KdsCallout,
  KdsCardFile,
  KdsDivider,
  KdsEmptyContent,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiAlertFill } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";
import { OrderItemStatusEnum } from "@/config/apiClient.types";
import { useFile } from "@/hooks/queries/useFile";
import { useIsOMS } from "@/hooks/useIsOMS";
import { ModalUploadProofForm } from "@/modals/ModalUploadProofForm";
import { type ProofBlocProps } from "./ProofBloc.types";

export const ProofBloc = ({ sandboxItemProof, orderItem }: ProofBlocProps) => {
  const { t } = useTranslate();
  const isOMS = useIsOMS();

  const [isOpenAddProof, setIsOpenAddProof] = useState(false);

  const { useDownloadFileHandler } = useFile();
  const { handleDownloadFile } = useDownloadFileHandler();

  const hasValidatedProof =
    !!sandboxItemProof?.validatedAt && !sandboxItemProof?.cancelledAt;
  const hasProofToValidate =
    !!sandboxItemProof?.refusedAt && hasValidatedProof && isOMS;

  return (
    <Box w="100%" rounded="br-md" bgColor="elevation.first" p="sp-md">
      <VStack gap="sp-md">
        <KdsText size="title-sm">
          {t("order.myOrdersItemDetails.fileProof")}
        </KdsText>

        {hasProofToValidate &&
          orderItem?.status !== OrderItemStatusEnum.CANCELLED && (
            <KdsCallout
              title={t("order.myOrdersItemDetails.callout.warningBat.title")}
              subtitle={t(
                "order.myOrdersItemDetails.callout.warningBat.content"
              )}
              iconLeft={RiAlertFill}
              has="button"
              buttonProps={{
                variant: "ghost",
                color: "warning",
                children: t("buttons.modify"),
                onPress: () => setIsOpenAddProof(true),
              }}
              state="warning"
            />
          )}

        {hasValidatedProof ? (
          sandboxItemProof?.files?.map((file, index) => (
            <VStack gap="sp-md" key={file.blobId}>
              {index !== 0 && <KdsDivider />}

              <KdsCardFile
                key={file.url}
                fileName={file.fileName}
                fileUrl={file.url}
                fileSize={file.size}
                isViewable
                isDownloadable
                onDownload={() => handleDownloadFile(file.fileName, file.url)}
              />

              {!!file.comment && (
                <Box px="sp-sm">
                  <VStack gap="sp-xs2">
                    <KdsText size="subtitle-sm">
                      {t("order.myOrdersItemDetails.specificRequest")}
                    </KdsText>

                    <div className="whitespace-pre-line">
                      <KdsText>{file.comment}</KdsText>
                    </div>
                  </VStack>
                </Box>
              )}
            </VStack>
          ))
        ) : (
          <KdsEmptyContent
            title={t("documentEmptyState.title")}
            description={t("documentEmptyState.subtitle")}
            linkProps={
              isOMS
                ? {
                    children: t("buttons.addProof"),
                    onPress: () => setIsOpenAddProof(true),
                    isDisabled: !orderItem.Workflow.PostponedActivities.some(
                      (postponedActivity) =>
                        postponedActivity.activity ===
                        WorkflowPostponedActivityReadDtoActivityEnum.AddProof
                    ),
                    href: "#",
                    state: "neutral",
                  }
                : undefined
            }
          />
        )}
      </VStack>

      <ModalUploadProofForm
        key={`upload-proof-${isOpenAddProof}`}
        orderId={orderItem?.orderId}
        orderItemId={orderItem?.id}
        isOpen={isOpenAddProof}
        setOpenState={setIsOpenAddProof}
      />
    </Box>
  );
};
